/**
 * Context Explain API - Fetches AI-generated contextual information
 * 
 * Provides contextual data cards with KPIs, EU taxonomy references,
 * remediation suggestions, and compliance impact analysis.
 */

export interface ContextCard {
  /** Primary KPI title */
  primaryKpi: string;
  /** Primary KPI value */
  primaryKpiValue: string;
  /** Secondary KPI title */
  secondaryKpi: string;
  /** Secondary KPI value */
  secondaryKpiValue: string;
  /** CO2e delta in kg (positive = increase, negative = reduction) */
  co2eDelta: number;
  /** Estimated cost savings in EUR */
  costSavingsEur: number;
  /** Estimated time to implement in days */
  timeDays: number;
  /** Implementation difficulty (1-5 scale) */
  difficulty: 1 | 2 | 3 | 4 | 5;
  /** EU Taxonomy Article reference */
  euArticleRef: string;
  /** Recommended remediation action */
  remediation: string;
  /** ESG slashing impact if compliance gap exists (percentage) */
  slashingImpact?: number;
}

export interface ContextExplainResponse {
  cards: ContextCard[];
  /** Overall compliance status */
  complianceStatus: "compliant" | "gap" | "critical";
  /** Warning message if compliance issues detected */
  warning?: string;
}

/**
 * Fetches contextual explanation data for a given route and input snapshot.
 * 
 * @param route - Active panel route identifier (e.g., "ets", "taxonomy", "futures")
 * @param inputSnapshot - Current input values snapshot
 * @returns Context explanation with data cards
 */
export async function fetchContextExplain(
  route: string,
  inputSnapshot: Record<string, number | string>
): Promise<ContextExplainResponse> {
  // MOCK implementation - later will connect to backend AI service
  await new Promise((resolve) => setTimeout(resolve, 400));

  // Route-specific mock responses
  if (route === "ets" || route === "etsbridge") {
    return {
      cards: [
        {
          primaryKpi: "EU ETS Compliance",
          primaryKpiValue: "87%",
          secondaryKpi: "Allowance Coverage",
          secondaryKpiValue: "124%",
          co2eDelta: -12500,
          costSavingsEur: 12400,
          timeDays: 14,
          difficulty: 2,
          euArticleRef: "EU Taxonomy Art. 9",
          remediation: "Bridge ETS registry evidence to AuditRegister. Verify MRV alignment.",
          slashingImpact: 5,
        },
      ],
      complianceStatus: "gap",
      warning: "ETS channel not officially verified. Registry verification required.",
    };
  }

  if (route === "taxonomy" || route === "taxonomyoracle") {
    return {
      cards: [
        {
          primaryKpi: "Taxonomy Alignment",
          primaryKpiValue: "72%",
          secondaryKpi: "Substantial Contribution",
          secondaryKpiValue: "68%",
          co2eDelta: -21000,
          costSavingsEur: 18900,
          timeDays: 30,
          difficulty: 3,
          euArticleRef: "EU Taxonomy Regulation Art. 3",
          remediation: "Switch top 10% suppliers to taxonomy-aligned alternatives. Update procurement criteria.",
        },
        {
          primaryKpi: "Do No Significant Harm",
          primaryKpiValue: "85%",
          secondaryKpi: "DNSH Compliance",
          secondaryKpiValue: "85%",
          co2eDelta: -8500,
          costSavingsEur: 11200,
          timeDays: 21,
          difficulty: 2,
          euArticleRef: "EU Taxonomy Regulation Art. 17",
          remediation: "Implement water efficiency audit. Reduce waste-to-landfill ratio by 15%.",
        },
      ],
      complianceStatus: "gap",
      warning: "Taxonomy alignment below 75% threshold. Remediation required for CSRD reporting.",
    };
  }

  if (route === "slasher" || route === "complianceslasher") {
    return {
      cards: [
        {
          primaryKpi: "Compliance Score",
          primaryKpiValue: "78%",
          secondaryKpi: "Slashing Risk",
          secondaryKpiValue: "HIGH",
          co2eDelta: -0,
          costSavingsEur: 0,
          timeDays: 7,
          difficulty: 1,
          euArticleRef: "ESRS E1-E3",
          remediation: "Fix Scope 2 emissions data gaps. Verify renewable PPA contracts.",
          slashingImpact: 12,
        },
      ],
      complianceStatus: "critical",
      warning: "Compliance slashing risk: 12% penalty if not resolved within 30 days.",
    };
  }

  if (route === "futures" || route === "futuresdesk") {
    return {
      cards: [
        {
          primaryKpi: "Hedge Ratio",
          primaryKpiValue: "45%",
          secondaryKpi: "Price Volatility",
          secondaryKpiValue: "18%",
          co2eDelta: -3200,
          costSavingsEur: 8900,
          timeDays: 3,
          difficulty: 1,
          euArticleRef: "MiFID II Art. 25",
          remediation: "Increase hedge ratio to 60% to reduce price exposure. Consider options for downside protection.",
        },
      ],
      complianceStatus: "compliant",
    };
  }

  if (route === "tenderscore" || route === "tenderscoring") {
    return {
      cards: [
        {
          primaryKpi: "Tender Score",
          primaryKpiValue: "84/100",
          secondaryKpi: "Ranking",
          secondaryKpiValue: "#3",
          co2eDelta: -15000,
          costSavingsEur: 45000,
          timeDays: 10,
          difficulty: 2,
          euArticleRef: "Public Procurement Directive 2014/24/EU",
          remediation: "Submit ESG certificate proof. Improve biodiversity impact score (+8 points available).",
        },
      ],
      complianceStatus: "compliant",
    };
  }

  if (route === "kms" || route === "kmsmanager") {
    return {
      cards: [
        {
          primaryKpi: "Key Rotation",
          primaryKpiValue: "Every 90d",
          secondaryKpi: "Encryption Standard",
          secondaryKpiValue: "AES-256",
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: 0,
          difficulty: 1,
          euArticleRef: "GDPR Art. 32",
          remediation: "Current KMS configuration is GDPR compliant.",
        },
      ],
      complianceStatus: "compliant",
    };
  }

  if (route === "custody" || route === "chainofcustody") {
    return {
      cards: [
        {
          primaryKpi: "Chain Integrity",
          primaryKpiValue: "100%",
          secondaryKpi: "Audit Trail Coverage",
          secondaryKpiValue: "98%",
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: 5,
          difficulty: 1,
          euArticleRef: "EU Blockchain Regulation Art. 13",
          remediation: "Complete audit trail gaps for remaining 2% of transactions.",
        },
      ],
      complianceStatus: "gap",
    };
  }

  if (route === "slasher" || route === "complianceslasher") {
    return {
      cards: [
        {
          primaryKpi: "Compliance Gap Score",
          primaryKpiValue: `${inputSnapshot.gapScore || 0}%`,
          secondaryKpi: "Severity",
          secondaryKpiValue: (inputSnapshot.severity as string) || "low",
          co2eDelta: -((inputSnapshot.gapScore as number) || 0) * 10,
          costSavingsEur: (inputSnapshot.remediationCostEUR as number) || 0,
          timeDays: (inputSnapshot.remediationDays as number) || 0,
          difficulty: 3,
          euArticleRef: "EU Taxonomy Art. 8",
          remediation: "Based on EU Taxonomy Article 8, slashing severity escalates due to supply-chain propagation. Implement cross-border compliance checks.",
          slashingImpact: 12,
        },
      ],
      complianceStatus: (inputSnapshot.severity as string) === "high" ? "critical" : "gap",
      warning: "Supply-chain depth increases downstream risk. Consider local offsets.",
    };
  }

  if (route === "scoperouter" || route === "scoperoute") {
    return {
      cards: [
        {
          primaryKpi: "Scope Category",
          primaryKpiValue: (inputSnapshot.scopeCategory as string) || "scope1",
          secondaryKpi: "Article Reference",
          secondaryKpiValue: (inputSnapshot.articleRef as string) || "A8",
          co2eDelta: -((inputSnapshot.supplyChainCount as number) || 0) * 500,
          costSavingsEur: ((inputSnapshot.supplyChainCount as number) || 0) * 0.3 * 1000,
          timeDays: 5,
          difficulty: 2,
          euArticleRef: `EU Taxonomy Art. ${(inputSnapshot.articleRef as string)?.replace("A", "") || "8"}`,
          remediation: `Supply-chain depth increases downstream risk by ${((inputSnapshot.supplyChainCount as number) || 0) * 5}%. Consider local offsets.`,
        },
      ],
      complianceStatus: "compliant",
    };
  }

  if (route === "smartoffset" || route === "offset") {
    return {
      cards: [
        {
          primaryKpi: "Offset Balance",
          primaryKpiValue: `${((inputSnapshot.offsetBalance as number) || 0).toLocaleString()}`,
          secondaryKpi: "Local Boost",
          secondaryKpiValue: `+${(((inputSnapshot.localOffsets as number) || 0) * 0.02).toFixed(2)}%`,
          co2eDelta: -((inputSnapshot.localOffsets as number) || 0) * 10,
          costSavingsEur: ((inputSnapshot.localOffsets as number) || 0) * 50,
          timeDays: 0,
          difficulty: 1,
          euArticleRef: "EU ETS Regulation Art. 11",
          remediation: "Local offsets provide higher credibility and lower fraud risk.",
        },
      ],
      complianceStatus: "compliant",
    };
  }

  if (route === "nationalsync" || route === "natsync") {
    return {
      cards: [
        {
          primaryKpi: "Ledger Height",
          primaryKpiValue: ((inputSnapshot.ledgerHeight as number) || 0).toLocaleString(),
          secondaryKpi: "Ack Status",
          secondaryKpiValue: (inputSnapshot.ackStatus as string) || "pending",
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: 0,
          difficulty: 1,
          euArticleRef: "EU Blockchain Regulation Art. 13",
          remediation: "Cross-border reconciliation latency affects ledger anchoring. Retry with exponential backoff.",
        },
      ],
      complianceStatus: (inputSnapshot.ackStatus as string) === "rejected" ? "gap" : "compliant",
      warning: (inputSnapshot.ackStatus as string) === "rejected" ? "Ledger sync failed. Retry required." : undefined,
    };
  }

  if (route === "scopeexchange" || route === "scope-exchange") {
    return {
      cards: [
        {
          primaryKpi: "Exchange Health",
          primaryKpiValue: `${((inputSnapshot.exchangeHealth as number) || 100).toFixed(1)}%`,
          secondaryKpi: "Integrity Score",
          secondaryKpiValue: `${((inputSnapshot.integrityScore as number) || 100)}%`,
          co2eDelta: -((inputSnapshot.outgoingPayloadKB as number) || 0) * 100,
          costSavingsEur: ((inputSnapshot.exchangeHealth as number) || 100) * 50,
          timeDays: (inputSnapshot.reconciliationDelayDays as number) || 0,
          difficulty: 2,
          euArticleRef: "EU Taxonomy Art. 8",
          remediation: "Downstream propagation increases Article 8 reconciliation friction. Optimize payload sizes and reduce reconciliation delays.",
        },
      ],
      complianceStatus: (inputSnapshot.integrityScore as number) >= 80 ? "compliant" : "gap",
      warning: (inputSnapshot.reconciliationDelayDays as number) > 7 ? "High reconciliation delay detected. Risk of compliance gap." : undefined,
    };
  }

  if (route === "eseal" || route === "peppol") {
    return {
      cards: [
        {
          primaryKpi: "Seal Confidence",
          primaryKpiValue: `${((inputSnapshot.sealConfidence as number) || 0)}%`,
          secondaryKpi: "Validator Region",
          secondaryKpiValue: (inputSnapshot.validatorRegion as string) || "EU",
          co2eDelta: 0,
          costSavingsEur: ((inputSnapshot.sealConfidence as number) || 0) * 10,
          timeDays: 0,
          difficulty: 1,
          euArticleRef: "EU eIDAS Regulation Art. 35",
          remediation: "PEPPOL anchor improves cross-border invoice admissibility. Ensure validator region matches transaction jurisdiction.",
        },
      ],
      complianceStatus: (inputSnapshot.sealConfidence as number) >= 70 ? "compliant" : "gap",
    };
  }

  if (route === "certificatenft" || route === "certificate-nft") {
    return {
      cards: [
        {
          primaryKpi: "Certificate Level",
          primaryKpiValue: ((inputSnapshot.certificateLevel as string) || "bronze").toUpperCase(),
          secondaryKpi: "ESG Score",
          secondaryKpiValue: `${((inputSnapshot.esgScore as number) || 0)}%`,
          co2eDelta: 0,
          costSavingsEur: ((inputSnapshot.esgScore as number) || 0) * 0.8,
          timeDays: ((inputSnapshot.validityDays as number) || 365),
          difficulty: 2,
          euArticleRef: "EU GPP Guidelines Art. 67",
          remediation: "Certificate level influences downstream tender preference under EU GPP guidelines. Higher levels improve competitive positioning.",
        },
      ],
      complianceStatus: ((inputSnapshot.certificateLevel as string) === "platinum" || (inputSnapshot.certificateLevel as string) === "gold") ? "compliant" : "gap",
    };
  }

  if (route === "eufabric" || route === "eu-fabric") {
    return {
      cards: [
        {
          primaryKpi: "Fabric Status",
          primaryKpiValue: `${((inputSnapshot.fabricStatus as number) || 100).toFixed(1)}%`,
          secondaryKpi: "Escalation Level",
          secondaryKpiValue: ((inputSnapshot.escalationLevel as string) || "info").toUpperCase(),
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: 0,
          difficulty: 3,
          euArticleRef: "EU Interoperability Framework Art. 12",
          remediation: "Cross-border packet friction degrades reconciliation throughput. Optimize replication lag and reduce anomaly flags.",
        },
      ],
      complianceStatus: ((inputSnapshot.escalationLevel as string) === "critical" ? "critical" : (inputSnapshot.escalationLevel as string) === "warning" ? "gap" : "compliant"),
      warning: ((inputSnapshot.escalationLevel as string) === "critical" || (inputSnapshot.escalationLevel as string) === "warning") ? "Fabric health degraded. Reconciliation errors detected." : undefined,
    };
  }

  if (route === "evidencechain" || route === "evidence-chain") {
    return {
      cards: [
        {
          primaryKpi: "Chain Integrity Score",
          primaryKpiValue: `${((inputSnapshot.chainIntegrityScore as number) || 100).toFixed(1)}%`,
          secondaryKpi: "Admissibility Level",
          secondaryKpiValue: ((inputSnapshot.admissibilityLevel as string) || "high").toUpperCase(),
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: 0,
          difficulty: 2,
          euArticleRef: "eIDAS Regulation Art. 14",
          remediation: "High admissibility indicates cross-border judicial compatibility under eIDAS. Maintain chain integrity above 80% for optimal admissibility.",
        },
      ],
      complianceStatus: ((inputSnapshot.admissibilityLevel as string) === "high") ? "compliant" : ((inputSnapshot.admissibilityLevel as string) === "medium") ? "gap" : "critical",
    };
  }

  if (route === "tendermesh" || route === "tender-mesh") {
    return {
      cards: [
        {
          primaryKpi: "Procurement Risk Score",
          primaryKpiValue: `${((inputSnapshot.procurementRiskScore as number) || 0).toFixed(1)}%`,
          secondaryKpi: "Escalation Level",
          secondaryKpiValue: ((inputSnapshot.escalationLevel as string) || "low").toUpperCase(),
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: 0,
          difficulty: 3,
          euArticleRef: "EU Public Procurement Directive 2014/24/EU",
          remediation: "Global mesh degradation increases systemic procurement volatility. Reduce conflict flags and corruption signals to improve integrity.",
        },
      ],
      complianceStatus: ((inputSnapshot.escalationLevel as string) === "high") ? "critical" : ((inputSnapshot.escalationLevel as string) === "medium") ? "gap" : "compliant",
      warning: ((inputSnapshot.escalationLevel as string) === "high" || (inputSnapshot.escalationLevel as string) === "medium") ? "Procurement risk elevated. Review conflict of interest flags." : undefined,
    };
  }

  if (route === "tribunal" || route === "procurement-tribunal") {
    return {
      cards: [
        {
          primaryKpi: "Tribunal Decision",
          primaryKpiValue: ((inputSnapshot.tribunalDecision as string) || "pending").toUpperCase(),
          secondaryKpi: "Remediation Demand",
          secondaryKpiValue: `€${((inputSnapshot.remediationDemandEUR as number) || 0).toLocaleString()}`,
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: ((inputSnapshot.appealWindowDays as number) || 0),
          difficulty: 3,
          euArticleRef: "EU GPP Guidelines Art. 67",
          remediation: "Tribunal escalation impacts subsidy eligibility under EU GPP. Appeal within the window to reduce remediation demand.",
        },
      ],
      complianceStatus: ((inputSnapshot.tribunalDecision as string) === "disqualification" ? "critical" : (inputSnapshot.tribunalDecision as string) === "fine" ? "gap" : (inputSnapshot.tribunalDecision as string) === "warning" ? "gap" : "compliant"),
      warning: ((inputSnapshot.tribunalDecision as string) === "disqualification" || (inputSnapshot.tribunalDecision as string) === "fine") ? "Tribunal decision rendered. Appeal window active." : undefined,
    };
  }

  if (route === "passport" || route === "passport-registry" || route === "esgpassport") {
    return {
      cards: [
        {
          primaryKpi: "Registry Health",
          primaryKpiValue: `${((inputSnapshot.registryHealth as number) || 100).toFixed(1)}%`,
          secondaryKpi: "Total Passports",
          secondaryKpiValue: ((inputSnapshot.passportCount as number) || 0).toString(),
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: 0,
          difficulty: 2,
          euArticleRef: "EU Taxonomy Regulation Art. 8",
          remediation: "Sovereign passports unlock premium tender preferences. Maintain high ESG scores to preserve passport levels.",
        },
      ],
      complianceStatus: ((inputSnapshot.registryHealth as number) || 100) >= 80 ? "compliant" : ((inputSnapshot.registryHealth as number) || 100) >= 50 ? "gap" : "critical",
    };
  }

  if (route === "subsidy" || route === "subsidy-eligibility") {
    const subsidyScore = (inputSnapshot.subsidyEligibilityScore as number) || 100;
    const escalationPath = (inputSnapshot.escalationPath as string) || "none";
    const riskOfMisuse = (inputSnapshot.riskOfMisuseFlag as boolean) || false;

    return {
      cards: [
        {
          primaryKpi: "Subsidy Eligibility Score",
          primaryKpiValue: `${subsidyScore.toFixed(1)}%`,
          secondaryKpi: "Grant Reduction",
          secondaryKpiValue: `${((inputSnapshot.grantReductionPercent as number) || 0).toFixed(1)}%`,
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: 0,
          difficulty: escalationPath === "prosecution" ? 5 : escalationPath === "euAudit" ? 4 : escalationPath === "regionalAudit" ? 3 : 1,
          euArticleRef: "EU State Aid Regulation Art. 9",
          remediation: "Eligibility degraded due to procurement volatility and corruption signals. Consider remediation under Article 9 to restore subsidy access.",
        },
      ],
      complianceStatus: subsidyScore >= 70 ? "compliant" : subsidyScore >= 50 ? "gap" : "critical",
      warning: riskOfMisuse ? "High risk of misuse detected. Escalation required." : escalationPath !== "none" ? "Escalation path activated. Audit recommended." : undefined,
    };
  }

  if (route === "beneficial" || route === "beneficial-graph" || route === "ownership-graph") {
    const conflictScore = (inputSnapshot.conflictScore as number) || 0;
    const oligarchClusterScore = (inputSnapshot.oligarchClusterScore as number) || 0;
    const integrityRisk = (inputSnapshot.integrityRisk as number) || 0;
    const nepotismFlags = (inputSnapshot.nepotismFlags as number) || 0;
    const tenderCollisions = (inputSnapshot.tenderCollisionCount as number) || 0;

    return {
      cards: [
        {
          primaryKpi: "Integrity Risk",
          primaryKpiValue: `${integrityRisk.toFixed(1)}%`,
          secondaryKpi: "Conflict Score",
          secondaryKpiValue: `${conflictScore.toFixed(1)}%`,
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: 0,
          difficulty: integrityRisk >= 70 ? 5 : integrityRisk >= 50 ? 4 : 3,
          euArticleRef: "EU State Aid Regulation Art. 9",
          remediation: "Oligarchic clustering increases governance downgrade and subsidy reduction. Disclose ownership structures and resolve conflicts of interest.",
        },
      ],
      complianceStatus: integrityRisk >= 70 ? "critical" : integrityRisk >= 50 ? "gap" : "compliant",
      warning: nepotismFlags > 0 || tenderCollisions > 0 
        ? `High risk detected: ${nepotismFlags} nepotism flags and ${tenderCollisions} tender collisions. Governance audit required.`
        : integrityRisk >= 50 
        ? "Moderate integrity risk. Review ownership structures."
        : undefined,
    };
  }

  if (route === "judicial" || route === "judicial-trigger") {
    const triggerSeverity = (inputSnapshot.triggerSeverity as number) || 0;
    const judicialAction = (inputSnapshot.judicialAction as string) || "MonitorOnly";
    const recommendedAuditorTier = (inputSnapshot.recommendedAuditorTier as number) || 1;
    const evidenceAdmissibility = (inputSnapshot.evidenceAdmissibility as string) || "medium";
    const tribunalDecision = (inputSnapshot.tribunalDecision as string) || "pending";
    const geopoliticalWeight = (inputSnapshot.geopoliticalWeight as number) || 0;

    return {
      cards: [
        {
          primaryKpi: "Judicial Action",
          primaryKpiValue: judicialAction,
          secondaryKpi: "Trigger Severity",
          secondaryKpiValue: `${triggerSeverity.toFixed(1)}%`,
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: 0,
          difficulty: recommendedAuditorTier,
          euArticleRef: "Anti-Corruption Directive 2014/42/EU",
          remediation: "Trigger escalation determined under Anti-Corruption Directive 2014/42/EU. Evidence admissibility affects prosecution viability.",
        },
      ],
      complianceStatus: triggerSeverity >= 75 ? "critical" : triggerSeverity >= 60 ? "gap" : triggerSeverity >= 40 ? "gap" : "compliant",
      warning: triggerSeverity >= 75 ? "EU-Prosecution level triggered. Immediate action required." : triggerSeverity >= 60 ? "National investigation recommended." : evidenceAdmissibility === "low" ? "Low evidence admissibility may affect case viability." : undefined,
    };
  }

  if (route === "satelliteoverlay" || route === "satellite-overlay") {
    const overlayRiskIndex = (inputSnapshot.overlayRiskIndex as number) || 0;
    const progressMismatch = (inputSnapshot.progressMismatch as number) || 0;
    const ghostSiteFlag = (inputSnapshot.ghostSiteFlag as boolean) || false;
    const satelliteDriftScore = (inputSnapshot.satelliteDriftScore as number) || 0;
    const procurementAnomaly = (inputSnapshot.procurementAnomaly as number) || 0;

    return {
      cards: [
        {
          primaryKpi: "Overlay Risk Index",
          primaryKpiValue: `${overlayRiskIndex.toFixed(1)}%`,
          secondaryKpi: "Progress Mismatch",
          secondaryKpiValue: `${progressMismatch.toFixed(1)}%`,
          co2eDelta: 0,
          costSavingsEur: 0,
          timeDays: 0,
          difficulty: overlayRiskIndex >= 75 ? 5 : overlayRiskIndex >= 50 ? 4 : overlayRiskIndex >= 25 ? 3 : 2,
          euArticleRef: "EU Cohesion Fund Regulation Art. 125",
          remediation: "Satellite drift and budget-progress mismatch indicate potential procurement fraud under EU Cohesion Fund rules. Verify site coordinates and budget allocation alignment.",
        },
      ],
      complianceStatus: overlayRiskIndex >= 75 ? "critical" : overlayRiskIndex >= 50 ? "gap" : overlayRiskIndex >= 25 ? "gap" : "compliant",
      warning: ghostSiteFlag 
        ? "⚠️ GHOST CONSTRUCTION ALERT: Construction progress <10% but budget disbursement >30%. Suspicious pattern detected."
        : overlayRiskIndex >= 75 
        ? "⚠️ High overlay risk detected. Potential procurement fraud indicators. Immediate audit required."
        : satelliteDriftScore >= 70 
        ? "High satellite drift detected. Verify site location and construction validity."
        : procurementAnomaly >= 70
        ? "High procurement anomaly detected. Review procurement process integrity."
        : undefined,
    };
  }

  // Default/fallback response
  return {
    cards: [
      {
        primaryKpi: "ESG Impact",
        primaryKpiValue: "Moderate",
        secondaryKpi: "Status",
        secondaryKpiValue: "Active",
        co2eDelta: 0,
        costSavingsEur: 0,
        timeDays: 0,
        difficulty: 1,
        euArticleRef: "General ESG Framework",
        remediation: "Provide more context by interacting with panel inputs.",
      },
    ],
    complianceStatus: "compliant",
  };
}

